import React from 'react'
import { Search, X, Filter } from 'lucide-react'
import { Button } from './Button'
import { Input } from './Input'

export interface SearchBarProps {
  /**
   * Search placeholder text
   */
  placeholder?: string
  /**
   * Search value
   */
  value?: string
  /**
   * On search change
   */
  onChange?: (value: string) => void
  /**
   * On search submit
   */
  onSubmit?: (value: string) => void
  /**
   * On clear search
   */
  onClear?: () => void
  /**
   * Show filter button
   */
  showFilter?: boolean
  /**
   * On filter click
   */
  onFilterClick?: () => void
  /**
   * Loading state
   */
  loading?: boolean
  /**
   * Disabled state
   */
  disabled?: boolean
  /**
   * Search suggestions
   */
  suggestions?: string[]
  /**
   * On suggestion select
   */
  onSuggestionSelect?: (suggestion: string) => void
  /**
   * Show suggestions
   */
  showSuggestions?: boolean
  /**
   * Custom className
   */
  className?: string
  /**
   * Auto focus
   */
  autoFocus?: boolean
}

const SearchBar = React.forwardRef<HTMLDivElement, SearchBarProps>(
  (
    {
      placeholder = 'Search...',
      value = '',
      onChange,
      onSubmit,
      onClear,
      showFilter = false,
      onFilterClick,
      loading = false,
      disabled = false,
      suggestions = [],
      onSuggestionSelect,
      showSuggestions = false,
      className = '',
      autoFocus = false,
      ...props
    },
    ref
  ) => {
    const [, setIsFocused] = React.useState(false)
    const [showSuggestionsList, setShowSuggestionsList] = React.useState(false)
    const inputRef = React.useRef<HTMLInputElement>(null)
    const suggestionsRef = React.useRef<HTMLDivElement>(null)

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value
      onChange?.(newValue)
      setShowSuggestionsList(showSuggestions && newValue.length > 0)
    }

    const handleSubmit = (e: React.FormEvent) => {
      e.preventDefault()
      onSubmit?.(value)
      setShowSuggestionsList(false)
    }

    const handleClear = () => {
      onChange?.('')
      onClear?.()
      inputRef.current?.focus()
    }

    const handleSuggestionClick = (suggestion: string) => {
      onChange?.(suggestion)
      onSuggestionSelect?.(suggestion)
      setShowSuggestionsList(false)
      inputRef.current?.focus()
    }

    const handleKeyDown = (e: React.KeyboardEvent) => {
      if (e.key === 'Escape') {
        setShowSuggestionsList(false)
        inputRef.current?.blur()
      }
    }

    React.useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (
          suggestionsRef.current &&
          !suggestionsRef.current.contains(event.target as Node) &&
          inputRef.current &&
          !inputRef.current.contains(event.target as Node)
        ) {
          setShowSuggestionsList(false)
        }
      }

      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }, [])

    return (
      <div ref={ref} className={`relative ${className}`} {...props}>
        <form onSubmit={handleSubmit} className="relative">
          <div className="relative">
            <Input
              ref={inputRef}
              type="text"
              placeholder={placeholder}
              value={value}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              onFocus={() => setIsFocused(true)}
              onBlur={() => setIsFocused(false)}
              leftIcon={<Search className="h-4 w-4" />}
              rightIcon={
                value ? (
                  <button
                    type="button"
                    onClick={handleClear}
                    className="text-gray-400 hover:text-gray-600 transition-colors"
                    tabIndex={-1}
                  >
                    <X className="h-4 w-4" />
                  </button>
                ) : undefined
              }
              disabled={disabled || loading}
              autoFocus={autoFocus}
              className="pr-12"
            />
            
            {showFilter && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={onFilterClick}
                className="absolute left-2 top-1/2 transform -translate-y-1/2"
                leftIcon={<Filter className="h-4 w-4" />}
              >
                Filter
              </Button>
            )}
          </div>
        </form>

        {/* Search Suggestions */}
        {showSuggestionsList && suggestions.length > 0 && (
          <div
            ref={suggestionsRef}
            className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-50 max-h-60 overflow-y-auto"
          >
            {suggestions.map((suggestion, index) => (
              <button
                key={index}
                type="button"
                className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 transition-colors first:rounded-t-lg last:rounded-b-lg"
                onClick={() => handleSuggestionClick(suggestion)}
              >
                <div className="flex items-center gap-2">
                  <Search className="h-4 w-4 text-gray-400" />
                  <span>{suggestion}</span>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    )
  }
)

SearchBar.displayName = 'SearchBar'

export { SearchBar }
